import { useState } from 'react'
import { DropdownMenuTrigger } from '@radix-ui/react-dropdown-menu'
import { FilterStore } from '../FilterStore'
import {
  DropdownMenu,
  DropdownMenuContent,
} from 'components/UI/BaseDropdown/dropdown-base'
import { cn } from 'components/UI/cn'
import {
  FILTER_POPOVER_BODY,
  FILTER_POPOVER_CONTENT_BASE,
  FILTER_POPOVER_FOOTER,
} from './popoverClasses'
import { RadioInput } from 'components/FormUtils/RadioInput'
import { useForm } from 'react-hook-form'
import FbButton from 'components/FbUI/FbButton'
import { FilterButton } from '../components/FilterButton'
import { FilterCloseButton } from '../components/FilterCloseButton'
import { useRegisterFilterKey } from '../components/FilterModal'
import { ZipChipInput } from '../inputs/ZipChipInput'

interface ZipcodeFilterProps {
  filterStore: FilterStore
}

export function ZipcodeFilter({ filterStore }: ZipcodeFilterProps) {
  const { pendingZipCodeFilter, setPendingZipCodeFilter } = filterStore()
  // Register the literal "zipcode" key so the auto-count helper picks up
  // the zip filter (the store keys it specially; see useFilterGroupActiveCount).
  useRegisterFilterKey('zipcode')

  const [open, setOpen] = useState(false)

  const clearFilters = () => {
    setPendingZipCodeFilter([], null)
  }

  const isActive = pendingZipCodeFilter[0].length > 0
  const radiusSet = pendingZipCodeFilter[1] != null

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <div className={'relative'}>
        <DropdownMenuTrigger asChild>
          <FilterButton
            open={open}
            isActive={isActive}
            title={radiusSet ? 'ZIP code radius' : 'ZIP code'}
          />
        </DropdownMenuTrigger>
        <FilterCloseButton
          isActive={isActive}
          open={open}
          onClick={clearFilters}
        />
      </div>
      <DropdownMenuContent
        forceMount={true}
        side={'bottom'}
        // Mobile cap (FIR-7449): see popoverClasses.ts.
        className={cn(FILTER_POPOVER_CONTENT_BASE, 'w-[280px] lg:w-[260px]')}
        collisionPadding={12}
        sideOffset={6}
      >
        {/*
         * Key-based remount: when the dropdown re-opens, ZipcodeFilterPanel
         * is recreated from scratch so its local zip list and radius form
         * are seeded fresh from pendingZipCodeFilter — no useEffect needed.
         */}
        <ZipcodeFilterPanel
          key={open ? 'open' : 'closed'}
          initialZips={pendingZipCodeFilter[0]}
          initialRadius={pendingZipCodeFilter[1]}
          isActive={isActive}
          onApply={(zips, radius) => {
            setPendingZipCodeFilter(zips, radius)
            setOpen(false)
          }}
          onClear={clearFilters}
          onCancel={() => setOpen(false)}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface ZipcodeFilterPanelProps {
  initialZips: string[]
  initialRadius: number | null
  isActive: boolean
  onApply: (zips: string[], radius: number | null) => void
  onClear: () => void
  onCancel: () => void
}

function ZipcodeFilterPanel({
  initialZips,
  initialRadius,
  isActive,
  onApply,
  onClear,
  onCancel,
}: ZipcodeFilterPanelProps) {
  const [zips, setZips] = useState<string[]>(initialZips)
  const { control, watch, reset } = useForm<{ mileRadius: number | null }>({
    defaultValues: { mileRadius: initialRadius },
  })

  const handleClear = () => {
    setZips([])
    reset({ mileRadius: null })
    onClear()
  }

  return (
    <>
      <div className={FILTER_POPOVER_BODY}>
        <ZipChipInput zips={zips} onChange={setZips} />
        <div className="text-xs font-semibold text-neutral-500 px-1 pt-3 pb-1">
          Radius
        </div>
        <div className="ml-2">
          <RadioInput
            options={[
              { label: 'Exact (no radius)', value: null },
              { label: '1 mile', value: 1 },
              { label: '5 miles', value: 5 },
              { label: '10 miles', value: 10 },
              { label: '15 miles', value: 15 },
              { label: '25 miles', value: 25 },
              { label: '50 miles', value: 50 },
              { label: '100 miles', value: 100 },
              { label: '200 miles', value: 200 },
            ]}
            formControl={control}
            fieldName={'mileRadius'}
          />
        </div>
      </div>
      <div
        className={cn(
          FILTER_POPOVER_FOOTER,
          'mt-3 items-center justify-between gap-2'
        )}
      >
        <button
          type="button"
          onClick={handleClear}
          disabled={zips.length === 0 && !isActive}
          className={cn(
            'text-sm font-medium transition-colors',
            zips.length === 0 && !isActive
              ? 'text-neutral-400 cursor-not-allowed'
              : 'text-brand-active hover:text-brand-bright'
          )}
        >
          Reset
        </button>
        <div className="flex gap-2">
          <FbButton
            size="sm"
            variant="secondary"
            type="button"
            onClick={onCancel}
          >
            Cancel
          </FbButton>
          <FbButton
            size="sm"
            type="button"
            className="!bg-brand-bright !text-white hover:!bg-brand-active active:!bg-brand-active disabled:!bg-neutral-100 disabled:!text-neutral-400 disabled:!border-neutral-100 disabled:hover:!bg-neutral-100"
            onClick={() => onApply(zips, watch('mileRadius'))}
            disabled={zips.length === 0}
          >
            Apply
          </FbButton>
        </div>
      </div>
    </>
  )
}

// Tells FilterModal's alphabetical sort how to label this composite. The
// filter title is hardcoded inside via FilterButton, so the modal can't read
// it off the JSX.
ZipcodeFilter.sortMeta = { title: 'Zip Code' }
